import {
  Avatar,
  Badge,
  CircularProgress,
  Grid,
  Input,
  Stack,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  ProfileUploadButton,
  ResetPasswordButton,
  StyledInputLabel,
  StyledOutlinedInput,
} from "../../pageComponents/settings/style";
import { StyledContainedButton } from "../modals/style";
import { CreateNewIcon, DeleteIcon } from "../../assets/Icons";
import UserAvatar from "../../assets/images/user-avatar1.png";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { AccountSettingsFormValuesProp } from "../../types/accountSettingsTypes";
import { useDispatch, useSelector } from "react-redux";
import { AccountSagaActions } from "../../store/account/sagas";
import {
  accountIsLoadingSelector,
  userDetailsSelector,
  userPhoneNumberSelector,
} from "../../store/account/selector";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import { AccountReducerAction } from "../../store/account/slice";
import { useNavigate } from "react-router-dom";
import routeConstant from "../../utils/routeConstant";

const AccountSettingsForm = () => {
  // States
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [profileImage, setProfileImage] = useState("");
  const [profileImageRemoved, setProfileImageRemoved] = useState(false);
  const [fileError, setFileError] = useState("");
  const [showError, setShowError] = useState(false);

  // Hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Selectors
  const user = useSelector(userDetailsSelector);
  const userPhoneNumber = useSelector(userPhoneNumberSelector);
  const loading = useSelector(accountIsLoadingSelector);
  const profileImageInputRef = useRef<HTMLInputElement>(null);

  // React Hook Forms
  const { register, handleSubmit, getValues, setValue, watch } =
    useForm<AccountSettingsFormValuesProp>({
      defaultValues: {
        fullName: user?.fullName,
        email: user?.email,
      },
    });

  useEffect(() => {
    if (user) {
      setValue("fullName", user.fullName);
      setValue("email", user.email);
      setProfileImage(user.profileImage);
    }
  }, [user, setValue]);

  // File validation function
  const validateFile = (file: File): string | null => {
    // Check file size (e.g., max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return "File size must be less than 5MB";
    }

    // Check if file is a video
    if (file.type.startsWith("video/")) {
      return "Video files are not allowed for profile pictures. Please select an image file (PNG, JPG, or JPEG).";
    }

    // Check if file is an allowed image type
    const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
    if (!allowedTypes.includes(file.type)) {
      return "Only PNG, JPG, and JPEG image files are allowed for profile pictures.";
    }

    return null; // No error
  };

  // Functions
  const handleProfileImageSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];

    if (file) {
      // Validate the file
      const validationError = validateFile(file);

      if (validationError) {
        // Show error message
        setFileError(validationError);
        setShowError(true);

        // Clear the input
        if (profileImageInputRef.current) {
          profileImageInputRef.current.value = "";
        }
        return;
      }

      // File is valid, proceed with selection
      setSelectedFile(file);
      setProfileImageRemoved(false);
      setFileError("");
    }
  };

  const handleRemoveProfilePicture = () => {
    setSelectedFile(null);
    setProfileImage("");
    setProfileImageRemoved(true);
    setFileError("");
    if (profileImageInputRef.current) {
      profileImageInputRef.current.value = "";
    }
  };

  const handleProfileUploadButtonClick = () => {
    profileImageInputRef.current?.click();
  };

  const handleNavigate = () => {
    navigate(routeConstant.dashboard, {
      replace: true,
    });
  };

  const handleResetPasswordModalAction = () => {
    dispatch(AccountReducerAction.setOpenResetPasswordModal(true));
  };

  const handleChange = (value: string) => {
    dispatch(AccountReducerAction.setUserPhoneNumber(value));
  };

  const handleCloseError = () => {
    setShowError(false);
    setFileError("");
  };

  const disableSaveButton =
    loading ||
    !watch("fullName") ||
    (watch("fullName") === user?.fullName &&
      !selectedFile &&
      !profileImageRemoved &&
      userPhoneNumber === user?.phoneNumber);

  const onSubmit = () => {
    if (disableSaveButton) return;
    const values = getValues();

    const formData = new FormData();

    formData.append("fullName", values.fullName);
    formData.append("phoneNumber", userPhoneNumber);

    if (selectedFile) {
      formData.append("profileImage", selectedFile);
    } else if (profileImageRemoved) {
      formData.append("profileImage", "null"); // Backend should handle this as a signal to remove
    }

    dispatch(
      AccountSagaActions.updateUserAccountDetails({
        id: user?._id,
        body: formData,
        handleNavigate: handleNavigate,
      })
    );
  };

  return (
    <div>
      {/* Error Snackbar */}
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseError}
          severity="error"
          sx={{ width: "100%" }}
        >
          {fileError}
        </Alert>
      </Snackbar>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "25px",
        }}
      >
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          sx={{ marginBottom: "2.5rem" }}
          badgeContent={
            <div>
              <Input
                type="file"
                style={{ display: "none" }}
                inputRef={profileImageInputRef}
                onChange={handleProfileImageSelect}
                inputProps={{ accept: ".png, .jpg, .jpeg" }}
              />
              <ProfileUploadButton onClick={handleProfileUploadButtonClick}>
                <CreateNewIcon fill="white" />
              </ProfileUploadButton>
            </div>
          }
        >
          <Avatar
            src={
              selectedFile
                ? URL.createObjectURL(selectedFile)
                : profileImage
                ? profileImage
                : UserAvatar
            }
            sx={{ width: "8.625rem", height: "8.625rem" }}
          />
        </Badge>
        <div
          style={{
            width: "1.5rem",
            height: "1.5rem",
            border: "1px solid red",
            borderRadius: "8px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            cursor: "pointer",
            marginBottom: "2.5rem",
          }}
          onClick={handleRemoveProfilePicture}
        >
          <DeleteIcon />
        </div>
      </div>
      <Grid container spacing={2.5}>
        <Grid item sm={6}>
          <StyledInputLabel htmlFor="fullName">Name</StyledInputLabel>
          <StyledOutlinedInput
            fullWidth
            id="fullName"
            type="text"
            {...register("fullName")}
          />
        </Grid>
        <Grid item sm={6}>
          <StyledInputLabel htmlFor="email">Email</StyledInputLabel>
          <StyledOutlinedInput
            fullWidth
            id="email"
            type="text"
            {...register("email")}
            disabled
            nonEditable
          />
        </Grid>
        <Grid item sm={6}>
          <StyledInputLabel htmlFor="phoneNumber">
            Phone Number
          </StyledInputLabel>
          <PhoneInput
            defaultCountry="usa"
            value={userPhoneNumber}
            onChange={(phone) => handleChange(phone)}
            placeholder="Enter phone number"
          />
        </Grid>
      </Grid>
      <Stack alignItems="end" gap="1.25rem" paddingTop="1.25rem">
        <ResetPasswordButton onClick={handleResetPasswordModalAction}>
          Change Password
        </ResetPasswordButton>
        <StyledContainedButton
          sx={{ paddingInline: "1.875rem" }}
          onClick={handleSubmit(onSubmit)}
          disabled={disableSaveButton}
          startIcon={loading && <CircularProgress size={20} color="inherit" />}
        >
          Save
        </StyledContainedButton>
      </Stack>
    </div>
  );
};

export default AccountSettingsForm;
